import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet } from '../services/api';

/**
 * Avisos da administração: a faixa no topo do sistema, para quem está logado.
 *
 * Quem decide o que está no ar é o servidor (GET /avisos/ativos/, entre o
 * início e o fim de cada aviso, e só os do perfil de quem está logado). A tela pergunta de novo a cada 5 minutos e,
 * quando o fim de um aviso chega, ele some sozinho — sem esperar a próxima
 * pergunta e sem a pessoa precisar recarregar a página.
 *
 * O ✕ esconde aquele aviso naquele aparelho (localStorage). Um aviso novo
 * aparece de novo, mesmo para quem fechou o anterior.
 */

const CHAVE_FECHADOS = 'sideb-avisos-fechados';
const INTERVALO_MS = 5 * 60 * 1000;

export const ESTILO_DO_NIVEL = {
  info: { fundo: '#0974BA', texto: '#ffffff', rotulo: 'Informativo' },
  atencao: { fundo: '#F59E0B', texto: '#1f2937', rotulo: 'Atenção' },
  urgente: { fundo: '#E83A3C', texto: '#ffffff', rotulo: 'Urgente' },
};

function lerFechados() {
  try {
    const lista = JSON.parse(localStorage.getItem(CHAVE_FECHADOS) || '[]');
    return Array.isArray(lista) ? lista : [];
  } catch (e) {
    return [];
  }
}

function gravarFechados(lista) {
  try {
    // Só os últimos 50: a lista não cresce para sempre.
    localStorage.setItem(CHAVE_FECHADOS, JSON.stringify(lista.slice(-50)));
  } catch (e) {
    // Navegação anônima ou armazenamento cheio: fecha só nesta visita.
  }
}

/** Uma faixa. Também usada na prévia do cadastro, em Configurações. */
export function FaixaAviso({ aviso, onFechar, className = '' }) {
  const estilo = ESTILO_DO_NIVEL[aviso.nivel] || ESTILO_DO_NIVEL.atencao;
  return (
    <div
      role="status"
      className={`flex items-start gap-3 rounded-xl px-4 py-3 shadow-lg ${className}`}
      style={{ background: estilo.fundo, color: estilo.texto }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 mt-0.5" fill="none"
        viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
      <div className="text-sm leading-snug min-w-0 flex-1">
        <div className="font-bold">Aviso da administração</div>
        <div className="whitespace-pre-line break-words">{aviso.mensagem}</div>
      </div>
      {onFechar && (
        <button
          type="button"
          onClick={onFechar}
          aria-label="Fechar aviso"
          title="Fechar aviso"
          className="flex-shrink-0 rounded-md px-1.5 text-lg leading-none opacity-70 hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  );
}

function FaixaDeAvisos({ className = '' }) {
  const [avisos, setAvisos] = useState([]);
  const [fechados, setFechados] = useState(lerFechados);
  const [agora, setAgora] = useState(() => Date.now());

  const buscar = useCallback(() => {
    apiGet('/avisos/ativos/')
      .then((lista) => {
        setAvisos(Array.isArray(lista) ? lista : []);
        setAgora(Date.now());
      })
      // Sem aviso não é erro: a faixa simplesmente não aparece.
      .catch(() => {});
  }, []);

  useEffect(() => {
    buscar();
    const id = setInterval(buscar, INTERVALO_MS);
    return () => clearInterval(id);
  }, [buscar]);

  const visiveis = useMemo(() => avisos.filter(
    (a) => !fechados.includes(a.id) && new Date(a.fim).getTime() > agora,
  ), [avisos, fechados, agora]);

  // O aviso some na hora do fim dele, e não só na próxima busca.
  useEffect(() => {
    if (!visiveis.length) return undefined;
    const proximoFim = Math.min(...visiveis.map((a) => new Date(a.fim).getTime()));
    const espera = Math.max(1000, Math.min(proximoFim - Date.now() + 500, INTERVALO_MS));
    const id = setTimeout(() => setAgora(Date.now()), espera);
    return () => clearTimeout(id);
  }, [visiveis]);

  const fechar = (id) => {
    const lista = [...fechados.filter((x) => x !== id), id];
    setFechados(lista);
    gravarFechados(lista);
  };

  if (!visiveis.length) return null;
  return (
    <div className={`space-y-2 ${className}`}>
      {visiveis.map((a) => (
        <FaixaAviso key={a.id} aviso={a} onFechar={() => fechar(a.id)} />
      ))}
    </div>
  );
}

export default FaixaDeAvisos;
